import type { FC } from "react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { TableControls } from "../../common/table/table-controls";
import "../../common/table/data-table.css";
import "../../index.css";
import { publishManager } from "./publish-manager";
import { proxy, useSnapshot } from "valtio";
import {
  ColDef,
  GetRowIdParams,
  GridApi,
  ICellRendererParams,
  IRowNode,
  ValueParserParams,
} from "ag-grid-community";
import {
  calculateEntityTableUpdates,
  calculateFieldValueUpdates,
} from "./calculate-updates";
import {
  ENTITY_TABLES,
  updateTypes,
  type DraftChangelogUpdate,
  type EntityRegistry,
  type EntityTable,
  type RowDiff,
} from "@renewmap/model";
import { Button } from "@renewmap/ui/button";
import { Info } from "lucide-react";
import { clsx } from "clsx";
import { Point } from "geojson";
import { selectedRowState, toRowWithGeom } from "./geom-info";
import { UnchangedLozenge } from "../../common/table/cell-renderers";
import { CustomCellEditorProps } from "ag-grid-react";

// Entity types with geometry columns for the geom box, mapped to their table
const entityTableByEntityType: Partial<Record<string, EntityTable>> = {
  project: "projects",
  substation: "substations_v1",
  network: "infrastructure_v5",
  gas: "infrastructure_v5",
};

// Custom DateTime Cell Editor using HTML5 datetime-local input with "Now" button
const DateTimeCellEditor = ({
  value,
  onValueChange,
  stopEditing,
}: CustomCellEditorProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const formatDateTimeLocal = (date: Date | null) => {
    if (!date) {
      return "";
    }
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [currentValue, setCurrentValue] = useState<string>(() =>
    formatDateTimeLocal(value),
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSetNow = () => {
    const now = new Date();
    const formatted = formatDateTimeLocal(now);
    setCurrentValue(formatted);
    onValueChange(now);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setCurrentValue(newValue);
    const newDate = newValue ? new Date(newValue) : null;
    onValueChange(newDate);
  };

  return (
    <div
      style={{
        display: "flex",
        gap: "4px",
        padding: "4px",
        height: "100%",
        alignItems: "center",
      }}
    >
      <input
        ref={inputRef}
        type="datetime-local"
        value={currentValue}
        onChange={handleChange}
        onBlur={() => stopEditing()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "Escape") {
            stopEditing();
          }
        }}
        className="ag-input-field-input ag-text-field-input"
        style={{ flex: 1, height: "100%", minWidth: 0 }}
      />
      <button
        type="button"
        onClick={handleSetNow}
        onMouseDown={(e) => e.preventDefault()} // Prevent blur on input
        style={{
          padding: "2px 8px",
          fontSize: "12px",
          cursor: "pointer",
          whiteSpace: "nowrap",
          backgroundColor: "#7EFBCD",
        }}
      >
        Now
      </button>
    </div>
  );
};

const entityNameCellRenderer = (
  props: ICellRendererParams<DraftChangelogUpdate, string | null> & {
    context: { entityRegistry: EntityRegistry };
  },
) => {
  if (!props.value) {
    return "";
  }
  const name =
    props.context.entityRegistry[props.value]?.entity_name ?? props.value;
  return (
    <div className="flex items-center p-1">
      <UnchangedLozenge label={name} />
    </div>
  );
};

export const updatesColumns: ColDef[] = [
  {
    field: "entity_id",
    headerName: "entity_id",
    width: 220,
    type: "text",
    editable: true,
    sort: "asc",
    pinned: "left",
    cellRenderer: entityNameCellRenderer,
    cellStyle: { backgroundColor: "white" },
  },
  {
    field: "title",
    headerName: "title",
    width: 350,
    type: "text",
    editable: true,
    pinned: "left",
    cellStyle: { backgroundColor: "white" },
  },
  {
    field: "update_type",
    headerName: "update_type",
    width: 150,
    cellEditor: "agSelectCellEditor",
    cellEditorParams: {
      values: updateTypes,
    },
    type: "text",
    editable: true,
    cellStyle: { backgroundColor: "white" },
  },
  {
    field: "timestamp",
    cellDataType: "dateTime",
    headerName: "timestamp",
    editable: true,
    cellEditor: DateTimeCellEditor,
    cellStyle: { backgroundColor: "white" },
    valueFormatter: (params) => {
      if (!params.value) {
        return "";
      }
      const date = new Date(params.value);
      return date.toLocaleString("en-AU", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
  },
  {
    field: "field_name",
    type: "text",
    headerName: "field_name",
    editable: false,
    cellStyle: { backgroundColor: "white" },
  },
  {
    field: "link_text",
    headerName: "link_text",
    type: "text",
    editable: true,
    hide: true,
    cellStyle: { backgroundColor: "white" },
  },
  {
    field: "link_url",
    headerName: "link_url",
    type: "text",
    editable: true,
    hide: true,
    cellStyle: { backgroundColor: "white" },
  },
  {
    field: "group_name",
    type: "text",
    headerName: "group_name",
    editable: true,
    hide: true,
    cellStyle: { backgroundColor: "white" },
  },
  {
    field: "group_rank",
    type: "number",
    headerName: "group_rank",
    editable: true,
    hide: true,
    cellStyle: { backgroundColor: "white" },
    valueParser: (params: ValueParserParams) => {
      if (!params.newValue) {
        return null;
      }
      const parsed = Number(params.newValue);
      return isNaN(parsed) ? params.oldValue : parsed;
    },
  },
  {
    field: "point",
    type: "text",
    headerName: "point",
    editable: false,
    hide: true,
    cellStyle: { backgroundColor: "white" },
    cellRenderer: (props: ICellRendererParams) => {
      const point = props.value as Point | null;
      if (!point) {
        return "";
      }

      return `${point.coordinates[0].toFixed(6)}, ${point.coordinates[1].toFixed(6)}`;
    },
  },
  {
    field: "zoom",
    type: "number",
    headerName: "zoom",
    editable: true,
    hide: true,
    cellStyle: { backgroundColor: "white" },
    valueParser: (params: ValueParserParams) => {
      if (!params.newValue) {
        return null;
      }
      const parsed = Number(params.newValue);
      return isNaN(parsed) ? params.oldValue : parsed;
    },
  },
  {
    field: "entity_type",
    headerName: "entity_type",
    type: "text",
    editable: true,
    hide: true,
    cellStyle: { backgroundColor: "white" },
  },
  {
    field: "linked_entity_id",
    headerName: "linked_entity_id",
    type: "text",
    editable: true,
    hide: true,
    cellRenderer: entityNameCellRenderer,
    cellStyle: { backgroundColor: "white" },
  },
  {
    field: "description",
    headerName: "description",
    type: "text",
    editable: true,
    hide: true,
    cellStyle: { backgroundColor: "white" },
  },
  {
    field: "old_value",
    headerName: "old_value",
    type: "text",
    editable: false,
    hide: true,
    cellStyle: { backgroundColor: "white" },
    valueFormatter: (params: { value: unknown }) =>
      params.value == null ? "" : String(params.value),
  },
  {
    field: "new_value",
    headerName: "new_value",
    type: "text",
    editable: false,
    hide: true,
    cellStyle: { backgroundColor: "white" },
    valueFormatter: (params: { value: unknown }) =>
      params.value == null ? "" : String(params.value),
  },
  {
    field: "publish",
    type: "boolean",
    headerName: "publish",
    width: 90,
    editable: true,
    pinned: "right",
    cellRenderer: "agCheckboxCellRenderer",
    cellEditor: "agCheckboxCellEditor",
    cellStyle: { backgroundColor: "white" },
  },
];

const hiddenColumns = updatesColumns
  .filter((col) => col.hide)
  .map((col) => col.field as string);

const LazyGrid = lazy(() => import("../../common/table/async-grid"));

// State to hold the callback for updating changelog points
export const updateChangelogPointState = proxy({
  updateFn: null as ((point: Point | null, zoom: number | null) => void) | null,
});

// Function to update changelog point - delegates to the component's state updater
export const setChangelogPoint = (point: Point | null, zoom: number | null) => {
  // Update the changelog point and zoom in selectedRowState for immediate UI update
  selectedRowState.changelogPoint = point;
  selectedRowState.changelogZoom = zoom;

  // Update the rowData state in the AG Grid
  if (updateChangelogPointState.updateFn) {
    updateChangelogPointState.updateFn(point, zoom);
  }
};

export const UpdateEntriesTable: FC = () => {
  const { types, canInsert, entityRegistry, fieldConfigs } = useSnapshot(
    publishManager.publishState,
  );

  const [quickSearchValue, setQuickSearch] = useState<string>("");
  const [inserting, setInserting] = useState<boolean>(false);
  const [showAllColumns, setShowAllColumns] = useState<boolean>(false);

  const initialRowData: DraftChangelogUpdate[] = useMemo(() => {
    if (!types.projects) {
      return [];
    }

    const mutableEntityRegistry = entityRegistry as EntityRegistry;

    const entityUpdates = ENTITY_TABLES.flatMap((table) =>
      calculateEntityTableUpdates(
        table,
        (types[table]?.entries ?? []) as RowDiff[],
        [...fieldConfigs],
        mutableEntityRegistry,
      ),
    );

    const newEntityIds = new Set(
      ENTITY_TABLES.flatMap(
        (table) => (types[table]?.entries ?? []) as RowDiff[],
      )
        .filter((change) => change.operation_type === "insert")
        .map((change) => change.id),
    );

    const fieldValueUpdates = calculateFieldValueUpdates(
      (types.field_values_v1?.entries ?? []) as RowDiff[],
      [...fieldConfigs],
      mutableEntityRegistry,
      newEntityIds,
    );

    return [...entityUpdates, ...fieldValueUpdates];
  }, [types, entityRegistry, fieldConfigs]);

  const gridContext = useMemo(
    () => ({ entityRegistry: entityRegistry as EntityRegistry }),
    [entityRegistry],
  );

  const [rowData, setRowData] = useState<DraftChangelogUpdate[]>([]);
  const gridApiRef = useRef<GridApi | null>(null);

  useEffect(() => {
    gridApiRef.current?.setColumnsVisible(hiddenColumns, showAllColumns);
  }, [showAllColumns]);

  useEffect(() => {
    setRowData(initialRowData);
  }, [initialRowData]);

  // Set up the update function that modifies rowData
  useEffect(() => {
    updateChangelogPointState.updateFn = (
      point: Point | null,
      zoom: number | null,
    ) => {
      const selectedRow = selectedRowState.selectedRow;
      if (!selectedRow || !selectedRow.id) {
        return;
      }

      // Extract the actual ID string from the ColumnDiff proxy
      const entityId =
        typeof selectedRow.id === "string"
          ? selectedRow.id
          : (selectedRow.id as { draft?: string; public?: string }).draft ||
            (selectedRow.id as { draft?: string; public?: string }).public;

      // Collect rows to update
      const updatedRows: DraftChangelogUpdate[] = [];

      setRowData((prevData) => {
        return prevData.map((row) => {
          // Update ALL changelog entries for this entity with the new point
          if (row.entity_id === entityId) {
            const updated = { ...row, point, zoom };
            updatedRows.push(updated);
            return updated;
          }
          return row;
        });
      });

      // Update AG Grid's internal state using applyTransaction
      if (gridApiRef.current && updatedRows.length > 0) {
        console.log("Applying transaction to AG Grid with rows:", updatedRows);
        gridApiRef.current.applyTransaction({ update: updatedRows });
      }

      // Also update toPublish if any of these rows are marked for publishing
      setToPublish((prevToPublish) => {
        return prevToPublish.map((row) => {
          if (row.entity_id === entityId) {
            return { ...row, point, zoom };
          }
          return row;
        });
      });
    };

    return () => {
      updateChangelogPointState.updateFn = null;
    };
  }, []);

  const [toPublish, setToPublish] = useState<DraftChangelogUpdate[]>([]);

  const insert = () => {
    setInserting(true);
    publishManager
      .insertDraftUpdates(toPublish)
      .then(() => {
        console.log("✓ Insert successful");
        setToPublish([]);
        setInserting(false);
      })
      .catch((error) => {
        console.error("✗ Insert failed:", error);
        setInserting(false);
      });
  };

  return (
    <>
      <div className="flex items-center gap-3">
        <div className="flex gap-4 p-1">
          <h2 className="m-auto text-lg font-semibold">Generated Updates</h2>
          <p
            className={clsx(
              "m-auto flex gap-1 text-sm",
              canInsert ? "" : "text-amber-600",
            )}
          >
            <Info className="m-auto h-4 w-4" />
            {canInsert
              ? "Check publish below to confirm which updates should be inserted into draft.changelog"
              : "You already inserted generated updates. Either publish or manually edit what you already did!"}
          </p>
        </div>
        <div className="ml-auto">
          <TableControls
            loading={false}
            setQuickSearch={setQuickSearch}
            showAllColumns={showAllColumns}
            setShowAllColumns={setShowAllColumns}
          />
        </div>
      </div>
      <div className="ag-grid-overrides h-[35vh] w-full overflow-hidden">
        <Suspense fallback={null}>
          <LazyGrid
            loading={false}
            animateRows={false}
            rowSelection={{
              mode: "multiRow",
              checkboxes: false,
              enableClickSelection: true,
            }}
            cellSelection={true}
            quickFilterText={quickSearchValue}
            context={gridContext}
            columnDefs={updatesColumns}
            rowData={canInsert ? rowData : []}
            getRowId={(params: GetRowIdParams<unknown>) => {
              const data = params.data as DraftChangelogUpdate;
              return `${data.entity_id}-${data.field_name}-${data.linked_entity_id ?? ""}-${data.timestamp?.getTime() || ""}`;
            }}
            onGridReady={(params) => {
              gridApiRef.current = params.api;
              params.api.setColumnsVisible(hiddenColumns, showAllColumns);
            }}
            onRowClicked={(event) => {
              const changelogUpdate = event.data as DraftChangelogUpdate;
              if (!changelogUpdate || !changelogUpdate.entity_id) {
                return;
              }

              // Set the type to changelog
              selectedRowState.type = "changelog";
              // Set the changelog's jittered point and zoom
              selectedRowState.changelogPoint = changelogUpdate.point;
              selectedRowState.changelogZoom = changelogUpdate.zoom;

              // Load the entity data for the geom box
              const table = changelogUpdate.entity_type
                ? entityTableByEntityType[changelogUpdate.entity_type]
                : undefined;
              const rowData = table
                ? publishManager.getEntry(table, changelogUpdate.entity_id)
                : undefined;

              selectedRowState.selectedRow = rowData
                ? toRowWithGeom(rowData)
                : null;
            }}
            onCellEditingStopped={(event) => {
              const toPublish: DraftChangelogUpdate[] = [];
              event.api.forEachLeafNode((row: IRowNode) => {
                if (row.data.publish) {
                  toPublish.push(row.data);
                }
              });
              setToPublish(toPublish);
            }}
          />
        </Suspense>
      </div>
      {toPublish.length > 0 && (
        <div className="flex w-full">
          <Button
            className="ml-auto flex gap-2"
            onClick={insert}
            disabled={inserting}
          >
            <p>
              Insert{" "}
              <strong>
                {toPublish.length}/{rowData.length}
              </strong>{" "}
              updates into draft.changelog
            </p>
          </Button>
        </div>
      )}
    </>
  );
};
